// PrivacyPolicy.js - Privacy Policy legal page
// Linked from footer, Signup, and ProviderSignup
// Styled to match the rest of the app
// Last updated: April 2026

import React from 'react';

function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <div className="legal-container">

        <a href="/" className="auth-logo">Evermark Memorial</a>

        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-effective">Last updated: April 2026</p>

        <div className="legal-section">
          <p>
            Evermark Memorial ("we," "us," or "our") is committed to protecting your privacy. This Privacy
            Policy describes what information we collect, how we use it, how we share it, and what
            choices you have. By using the Evermark Memorial Platform you agree to the collection and use of
            information as described here.
          </p>
        </div>

        {/* 1. Information We Collect */}
        <div className="legal-section">
          <h2>1. Information We Collect</h2>
          <p>We collect the following types of information when you use Evermark Memorial:</p>
          <ul>
            <li>
              <strong>Account information:</strong> Your first name, last name, and email address
              when you register. Passwords are managed entirely by Firebase Authentication and are
              hashed using industry-standard encryption — Evermark Memorial never stores passwords in plain text
              and has no access to your password.
            </li>
            <li>
              <strong>Profile information:</strong> Optional profile photo, phone number (Caretakers),
              service city and state (Caretakers), and a short bio (Caretakers). Customers may also
              upload an optional profile photo.
            </li>
            <li>
              <strong>Booking information:</strong> Loved one's name, cemetery name and address,
              cemetery city and state, service type, headstone size, preferred visit date, and any
              special instructions you provide. This information is stored in Firestore and linked
              to your account.
            </li>
            <li>
              <strong>Payment information:</strong> All payment transactions are handled entirely by
              Stripe, Inc. Evermark Memorial never stores, sees, or has access to credit card numbers, bank
              account numbers, or full payment details. We receive only a payment confirmation status
              and a transaction reference ID from Stripe.
            </li>
            <li>
              <strong>Visit photos:</strong> Before and after photos submitted by Caretakers upon
              job completion are stored in Firebase Storage and linked to the corresponding booking.
              On Tribute Visit jobs, receipt photos are also stored.
            </li>
            <li>
              <strong>Messages:</strong> In-app messages between Customers and Caretakers are stored
              in Firestore and are associated with the relevant booking conversation.
            </li>
            <li>
              <strong>Usage data:</strong> We may collect standard analytics data including pages
              visited, features used, session duration, and device type to help us understand and
              improve the Platform.
            </li>
            <li>
              <strong>Location data:</strong> We collect city and state from Customers at booking and
              from Caretakers during signup for the purpose of matching jobs by geographic area only.
              We do not collect precise GPS location or track your physical location in real time.
            </li>
          </ul>
        </div>

        {/* 2. How We Use Your Information */}
        <div className="legal-section">
          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Create and maintain your account and authenticate your identity</li>
            <li>
              Match Customer bookings with available Caretakers in the same city and state
            </li>
            <li>Process bookings and payments through Stripe</li>
            <li>Deliver before and after photo reports to Customers upon job completion</li>
            <li>
              Send transactional emails including booking confirmations, caretaker assignment
              notifications, and visit completion alerts
            </li>
            <li>Enable in-app messaging between Customers and their assigned Caretaker</li>
            <li>Calculate and display Caretaker ratings and review summaries</li>
            <li>
              Enforce our Terms of Service and investigate suspected violations or fraudulent activity
            </li>
            <li>Improve the Platform, fix technical issues, and develop new features</li>
          </ul>
          <p>
            <strong>We do not sell your personal data to third parties.</strong> We do not use your
            data for advertising purposes, and we do not share your data with advertising networks
            or data brokers.
          </p>
        </div>

        {/* 3. How We Share Your Information */}
        <div className="legal-section">
          <h2>3. How We Share Your Information</h2>
          <p>
            We share your information only in the following limited circumstances:
          </p>
          <ul>
            <li>
              <strong>With your matched Caretaker:</strong> When a Caretaker claims your job, they
              receive your first name, the loved one's name, cemetery name and location, service type,
              preferred date, and any special instructions. Caretakers do not receive your email
              address, payment information, or full last name.
            </li>
            <li>
              <strong>Stripe:</strong> We share payment data with Stripe, Inc. for the purpose of
              processing your transaction. Stripe operates under its own privacy policy and is PCI
              DSS compliant.
            </li>
            <li>
              <strong>EmailJS:</strong> We use EmailJS to deliver transactional email notifications.
              Relevant account and booking information is passed to EmailJS for email delivery only.
            </li>
            <li>
              <strong>Legal compliance:</strong> We may disclose information if required by law,
              court order, or government authority, or to protect the rights, property, or safety
              of Evermark Memorial, our users, or the public.
            </li>
          </ul>
          <p>
            We do not share your personal data with any other third parties beyond those listed above.
          </p>
        </div>

        {/* 4. Data Storage and Security */}
        <div className="legal-section">
          <h2>4. Data Storage and Security</h2>
          <p>
            Your account data, booking records, messages, and profile information are stored in
            Google Firebase Firestore, a cloud-hosted NoSQL database operated by Google LLC. Firebase
            uses industry-standard encryption and access controls to protect stored data.
          </p>
          <p>
            Visit photos are stored in Firebase Storage, also operated by Google LLC, with access
            controls tied to authenticated user sessions.
          </p>
          <p>
            Payments are processed and stored by Stripe, Inc., which is certified as a PCI Service
            Provider Level 1 — the highest level of payment security certification.
          </p>
          <p>
            All data transmission between your browser and the Evermark Memorial Platform is encrypted via
            HTTPS using TLS. Firebase Authentication handles all password management and login
            session security.
          </p>
          <p>
            While we implement reasonable security measures, no system is completely secure. We cannot
            guarantee absolute security of your data and are not responsible for unauthorized access
            resulting from factors outside our reasonable control.
          </p>
        </div>

        {/* 5. Photos and Visit Records */}
        <div className="legal-section">
          <h2>5. Photos and Visit Records</h2>
          <p>
            Before and after visit photos uploaded by Caretakers are stored in Firebase Storage and
            are linked to the specific booking. These photos are accessible to the Customer who
            placed the booking and to the assigned Caretaker. Evermark Memorial administrators may access
            photos for dispute resolution and quality review purposes.
          </p>
          <p>
            Evermark Memorial may use anonymized, non-identifiable visit photos for the purpose of improving
            the Platform, training internal quality standards, or demonstrating service examples to
            prospective users. Any such use will not include personally identifiable information such
            as customer names, gravesite identifiers, or location details.
          </p>
        </div>

        {/* 6. Your Rights */}
        <div className="legal-section">
          <h2>6. Your Rights</h2>
          <p>You have the following rights regarding your personal data:</p>
          <ul>
            <li>
              <strong>Access:</strong> You can view your account information and booking history at
              any time through your profile on the Platform.
            </li>
            <li>
              <strong>Correction:</strong> You can update your name, profile photo, and other profile
              details through your profile settings.
            </li>
            <li>
              <strong>Deletion:</strong> You may request deletion of your account and associated
              personal data by contacting us through the Contact page. We will process deletion
              requests within 30 days, subject to any legal retention requirements for financial or
              booking records.
            </li>
            <li>
              <strong>Email opt-out:</strong> You may opt out of non-transactional emails by
              contacting us. Note that you cannot opt out of transactional emails directly related to
              your active bookings (such as booking confirmations and caretaker assignment notices).
            </li>
          </ul>
          <p>
            To exercise any of these rights, contact us through the{' '}
            <a href="/contact">Contact page</a> on the Platform.
          </p>
        </div>

        {/* 7. Cookies and Tracking */}
        <div className="legal-section">
          <h2>7. Cookies and Tracking</h2>
          <p>
            Evermark Memorial uses browser cookies and local storage primarily for authentication session
            management — to keep you logged in across page loads and browser sessions. Firebase
            Authentication relies on these mechanisms to maintain your login state securely.
          </p>
          <p>
            We may use analytics tools to understand how the Platform is used — including which pages
            are visited, how long sessions last, and what features are used most frequently. This data
            is used solely to improve the Platform and is not used for advertising.
          </p>
          <p>
            We do not use advertising cookies, tracking pixels, or third-party advertising networks.
            You can disable cookies in your browser settings, but doing so may prevent you from
            staying logged in or using certain Platform features.
          </p>
        </div>

        {/* 8. Children's Privacy */}
        <div className="legal-section">
          <h2>8. Children's Privacy</h2>
          <p>
            The Evermark Memorial Platform is intended for users who are 18 years of age or older. We do not
            knowingly collect personal information from anyone under the age of 18. If we become aware
            that a minor has created an account, we will take steps to delete the account and
            associated data promptly. If you believe a minor has used our Platform, please contact us
            through the <a href="/contact">Contact page</a>.
          </p>
        </div>

        {/* 9. Changes to This Policy */}
        <div className="legal-section">
          <h2>9. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            technology, legal requirements, or other factors. When we make material changes, we will
            update the date at the top of this page. We may also notify registered users via email
            for significant changes.
          </p>
          <p>
            Your continued use of the Platform after changes are posted constitutes your acceptance
            of the updated Privacy Policy. If you do not agree with changes, you may request account
            deletion through the Contact page.
          </p>
        </div>

        {/* 10. Contact */}
        <div className="legal-section">
          <h2>10. Contact</h2>
          <p>
            For questions, concerns, or requests related to this Privacy Policy or your personal data,
            please contact us through the{' '}
            <a href="/contact">Contact page</a> on the Evermark Memorial Platform. We will respond to
            privacy-related inquiries within a reasonable time.
          </p>
        </div>

        <p className="legal-footer-note">
          Also see our <a href="/terms">Terms of Service</a> and{' '}
          <a href="/contact">Contact &amp; Support</a> page.
        </p>

      </div>
    </div>
  );
}

export default PrivacyPolicy;
